import React from 'react';

// Inline stroke icons mirroring design_handoff_bsm_portal/prototype/icons.jsx.
// Stroke-based, 24×24 viewBox, currentColor.  Default rendered at 16px.
//
// To swap in a third-party icon library (Lucide is the recommended drop-in
// per the handoff), implement a new Icon component with the same prop API
// and the call-sites do not change.

export type IconName =
  | 'dashboard'
  | 'briefcase'
  | 'users'
  | 'award'
  | 'folder'
  | 'building'
  | 'user'
  | 'calendar'
  | 'bell'
  | 'search'
  | 'filter'
  | 'download'
  | 'upload'
  | 'plus'
  | 'more'
  | 'arrow'
  | 'back'
  | 'check'
  | 'clock'
  | 'pin'
  | 'doc'
  | 'mail'
  | 'cards'
  | 'rows';

interface Props {
  name: IconName;
  size?: number;
  stroke?: number;
  className?: string;
}

const PATHS: Record<IconName, React.ReactNode> = {
  dashboard: (
    <>
      <rect x="3" y="3" width="7" height="9" rx="1.2" />
      <rect x="14" y="3" width="7" height="5" rx="1.2" />
      <rect x="14" y="12" width="7" height="9" rx="1.2" />
      <rect x="3" y="16" width="7" height="5" rx="1.2" />
    </>
  ),
  briefcase: (
    <>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
      <path d="M3 13h18" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20c.7-3.5 3.4-5.5 6.5-5.5s5.8 2 6.5 5.5" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M16 14.5c2.5.3 4.4 2.1 5 5" />
    </>
  ),
  award: (
    <>
      <circle cx="12" cy="9" r="6" />
      <path d="M8.5 14L7 21l5-2.5L17 21l-1.5-7" />
    </>
  ),
  folder: (
    <path d="M3 6.5A1.5 1.5 0 0 1 4.5 5h4l2 2.5h9A1.5 1.5 0 0 1 21 9v9.5A1.5 1.5 0 0 1 19.5 20h-15A1.5 1.5 0 0 1 3 18.5z" />
  ),
  building: (
    <>
      <rect x="4" y="3" width="16" height="18" rx="1" />
      <path d="M9 8h2M13 8h2M9 12h2M13 12h2M9 16h2M13 16h2" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c1.5-4.5 4.5-7 8-7s6.5 2.5 8 7" />
    </>
  ),
  calendar: (
    <>
      <rect x="3.5" y="5" width="17" height="15" rx="1.5" />
      <path d="M3.5 10h17M8 3v4M16 3v4" />
    </>
  ),
  bell: (
    <>
      <path d="M6 9a6 6 0 1 1 12 0c0 4 1.5 5 2 6H4c.5-1 2-2 2-6z" />
      <path d="M10 19a2 2 0 0 0 4 0" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4 4" />
    </>
  ),
  filter: <path d="M3 5h18l-7 9v6l-4-2v-4z" />,
  download: <path d="M12 4v11m0 0l-4-4m4 4l4-4M4 19h16" />,
  upload: <path d="M12 19V8m0 0l-4 4m4-4l4 4M4 5h16" />,
  plus: <path d="M12 4v16M4 12h16" />,
  more: (
    <>
      <circle cx="5" cy="12" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="19" cy="12" r="1.5" />
    </>
  ),
  arrow: <path d="M5 12h14m0 0l-5-5m5 5l-5 5" />,
  back: <path d="M19 12H5m0 0l5 5m-5-5l5-5" />,
  check: <path d="M5 12l5 5L20 7" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v5l3.5 2" />
    </>
  ),
  pin: <path d="M12 17v5M9 3h6l-1 6 3 4H7l3-4z" />,
  doc: (
    <>
      <path d="M7 3h8l4 4v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
      <path d="M14 3v5h5" />
    </>
  ),
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="1.5" />
      <path d="M3 7l9 6 9-6" />
    </>
  ),
  cards: (
    <>
      <rect x="3" y="4" width="7" height="7" rx="1" />
      <rect x="14" y="4" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </>
  ),
  rows: (
    <>
      <rect x="3" y="5" width="18" height="3.5" rx="0.7" />
      <rect x="3" y="10.2" width="18" height="3.5" rx="0.7" />
      <rect x="3" y="15.5" width="18" height="3.5" rx="0.7" />
    </>
  )
};

const Icon = ({ name, size = 16, stroke = 1.5, className = 'ico' }: Props) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {PATHS[name]}
  </svg>
);

export default Icon;
